export type NormalizedZoneGeojson =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

/** Patrón para detectar WKB en formato hex (backend no debería enviar esto) */
const WKB_HEX_REGEX = /^[0-9a-fA-F]+$/;

function isPolygon(
  g: { type?: string; coordinates?: unknown }
): g is { type: 'Polygon'; coordinates: number[][][] } {
  return (
    g?.type === 'Polygon' &&
    Array.isArray(g.coordinates) &&
    g.coordinates.length > 0 &&
    Array.isArray(g.coordinates[0])
  );
}

function isMultiPolygon(
  g: { type?: string; coordinates?: unknown }
): g is { type: 'MultiPolygon'; coordinates: number[][][][] } {
  return (
    g?.type === 'MultiPolygon' &&
    Array.isArray(g.coordinates) &&
    g.coordinates.length > 0
  );
}

export function normalizeZoneGeojson(geojson: unknown): NormalizedZoneGeojson | null {
  if (geojson == null) return null;

  let obj: Record<string, unknown> | null = null;

  if (typeof geojson === 'string') {
    const trimmed = geojson.trim();
    if (!trimmed) return null;
    if (WKB_HEX_REGEX.test(trimmed)) return null;
    try {
      obj = JSON.parse(geojson) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (typeof geojson === 'object' && geojson !== null) {
    obj = geojson as Record<string, unknown>;
  }

  if (!obj) return null;

  const geom =
    obj.type === 'Feature' && obj.geometry && typeof obj.geometry === 'object'
      ? (obj.geometry as Record<string, unknown>)
      : obj;

  const type = geom?.type;
  const coordinates = geom?.coordinates;

  if (!type || !coordinates) return null;

  const candidate = { type, coordinates } as {
    type: string;
    coordinates: number[][][] | number[][][][];
  };

  if (isPolygon(candidate)) return candidate;
  if (isMultiPolygon(candidate as { type: string; coordinates: number[][][][] }))
    return candidate as NormalizedZoneGeojson;

  return null;
}

/** Convierte par [a,b] a [lng, lat] GeoJSON corrigiendo lat/lng invertidos. */
function fixCoordinatePair(a: number, b: number): [number, number] {
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return [a, b];
  }

  const absA = Math.abs(a);
  const absB = Math.abs(b);

  // Sudamérica operativa (UY, PE): ambos negativos; |lng| suele ser mayor
  // que |lat|. El tope de 85 cubre longitudes peruanas (~-81) sin dejar de
  // filtrar valores fuera de rango. Antes estaba en 65 y saltaba el swap en
  // Perú, dejando lat/lng invertidos.
  if (a < 0 && b < 0 && absA <= 85 && absB <= 85) {
    if (absA < absB && absA <= 40) {
      return [b, a];
    }
    return [a, b];
  }

  return [a, b];
}

function fixRing(ring: number[][]): number[][] {
  return ring.map(([a, b]) => {
    const [lng, lat] = fixCoordinatePair(a, b);
    return [lng, lat];
  });
}

export function fixNormalizedZoneGeojson(
  geojson: NormalizedZoneGeojson
): NormalizedZoneGeojson {
  if (geojson.type === 'Polygon') {
    return {
      type: 'Polygon',
      coordinates: geojson.coordinates.map((ring) => fixRing(ring)),
    };
  }

  return {
    type: 'MultiPolygon',
    coordinates: geojson.coordinates.map((poly) =>
      poly.map((ring) => fixRing(ring))
    ),
  };
}

/** Centro aproximado del polígono en [lat, lng] para validar región. */
export function getNormalizedGeojsonCenter(
  geojson: NormalizedZoneGeojson
): { lat: number; lng: number } | null {
  const ring =
    geojson.type === 'Polygon'
      ? geojson.coordinates[0]
      : geojson.coordinates[0]?.[0];

  if (!ring?.length) return null;

  let sumLat = 0;
  let sumLng = 0;
  let count = 0;

  ring.forEach(([lng, lat]) => {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    sumLng += lng;
    sumLat += lat;
    count += 1;
  });

  if (count === 0) return null;
  return { lat: sumLat / count, lng: sumLng / count };
}

export function toLeafletLatLngRings(
  geojson: NormalizedZoneGeojson
): [number, number][][] {
  if (geojson.type === 'Polygon') {
    return [geojson.coordinates[0].map(([lng, lat]) => [lat, lng])];
  }
  return geojson.coordinates.map((poly) =>
    poly[0].map(([lng, lat]) => [lat, lng])
  );
}
